/**
 * @file useLegalTargetList.ts
 * @description Подписка на общий список документов и групп магазина
 * (phase19.16, партия «Юридические документы»; `legal-targets-store`):
 * список, признак ошибки и перечитывание после сброса. Без прав и
 * уведомлений — годится и строке меню («Документ» / «Группа документов»).
 *
 * `enabled = false` — не грузить (строка меню другого вида); `fresh` —
 * при открытии запросить заново, а не взять общий ответ.
 */

import { useEffect, useState } from "react";

import { loadLegalTargets, type LegalTarget } from "../../lib/api-legal-targets";
import { subscribeLegalTargets } from "../../lib/site-view/legal-targets-store";

export interface LegalTargetList {
  /** null — ещё грузится, ошибка или не нужен. */
  targets: LegalTarget[] | null;
  failed: boolean;
}

export function useLegalTargetList({ enabled = true, fresh = false }: { enabled?: boolean; fresh?: boolean } = {}): LegalTargetList {
  const [targets, setTargets] = useState<LegalTarget[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Номер загрузки: растёт при сбросе общего списка — перечитать.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => (enabled ? subscribeLegalTargets(() => setAttempt((a) => a + 1)) : undefined), [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setFailed(false);
    loadLegalTargets(fresh && attempt === 0)
      .then((items) => !cancelled && setTargets(items))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [attempt, enabled, fresh]);

  return { targets, failed };
}
